/**
 * Class to handle creating and drawing on a canvas element via its 2D context.
 *
 * @module CanvasContext
 */
var CanvasContext = (function() {

  /**
   * The constructor for the CanvasContext class.
   * @constructor
   */
  function CanvasContext(options) {

    this.element = this.createCanvasElement(options);
    this.context = this.createCanvasContext(this.element, options);

  }

  /**
   * Method to create the canvas element.
   */
  CanvasContext.prototype.createCanvasElement = function(options) {

    options = options || {};

    var canvas = document.createElement("canvas");

    // Set the canvas render width/height to the full size
    canvas.width = options.width || 1000;
    canvas.height = options.height || 1000;

    // Set the canvas display width/heigth (CSS) to 10% of the full size
    canvas.style.width = canvas.width + "px";
    canvas.style.height = canvas.height + "px";

    // HACK: To be removed
    canvas.style.display = "block";
    canvas.style.backgroundColor = "#f9f9f9";
    canvas.style.border = "1rem solid #eee";
    canvas.style.margin = "10rem auto";

    return canvas;

  };

  /**
   * Method to create the canvas context.
   */
  CanvasContext.prototype.createCanvasContext = function(canvas, options) {

    options = options || {};

    var context = canvas.getContext("2d");

    // Set background color
    if (options.fill) {
      context.fillStyle = options.fill;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }

    // TODO: Does not work on Firefox, but could if using offline canvas context
    context.imageSmoothingQuality = "high";

    return context;

  };

  /**
   * Method to draw an image src by loading the image first.
   */
  CanvasContext.prototype.drawImageSrc = function(options) {

    options = options || {};

    var self = this;

    this.loadImage(options.src, function(image) {
      options.image = image;
      self.drawImage(options);
      options.callback && options.callback();
    });

  };

  /**
   * Method to draw an image.
   */
  CanvasContext.prototype.drawImage = function(options) {

    options = options || {};

    var scale = options.scale || 1;
    var width = (options.width || options.image.width) * scale;
    var height = (options.height || options.image.height) * scale;
    var x = options.x || 0;
    var y = options.y || 0;

    this.context.drawImage(options.image, x, y, width, height);

  };

  /**
   * Method to draw a rectangle on the canvas.
   */
  CanvasContext.prototype.drawRectangle = function(options) {

    options = options || {};

    this.context.fillStyle = options.fill;

    this.context.fillRect(
      options.x || 0,
      options.y || 0,
      options.width,
      options.height
    );

  };

  /**
   * Method to draw a circle on the canvas.
   */
  CanvasContext.prototype.drawCircle = function(options) {

    options = options || {};

    var radius = options.radius;
    var x = options.x || radius;
    var y = options.y || radius;
    var start = options.start || 0;
    var end = options.end || Math.PI * 2;

    this.context.fillStyle = options.fill;

    this.context.beginPath();
    this.context.arc(x, y, radius, start, end);
    this.context.closePath();
    this.context.fill();

  };

  /**
   * Method to draw a circle mask on the canvas.
   */
  CanvasContext.prototype.drawCircularMask = function(options) {

    options = options || {};

    this.context.globalCompositeOperation = "destination-in";

    this.drawCircle(options);

  };

  /**
   * Method to draw a circle on the canvas.
   */
  CanvasContext.prototype.drawText = function(options) {

    options = options || {};

    this.context.font = options.font;
    this.context.fillStyle = options.fill;
    this.context.textAlign = "center";
    this.context.textBaseline = "top";
    this.context.fillText(options.value, options.x, options.y);

  };

  /**
   * Method to load an image.
   */
  CanvasContext.prototype.loadImage = function(src, callback) {

    var image = document.createElement("img");

    image.onload = function() {
      callback(image);
    };

    image.src = src;

  };

  /**
   * Method to render the profile image to the base canvas.
   * @see: https://stackoverflow.com/questions/45866873
   */
  CanvasContext.prototype.trimCanvas = function() {

    var w = this.element.width;
    var h = this.element.height;

    if (!w && !h) {
      return false;
    }

    var imageData = this.context.getImageData(0, 0, w, h);
    var pixelData = new Uint32Array(imageData.data.buffer);
    var indexA = 0;
    var indexB = w * h - 1;
    var found = false;
    var top = 0;
    var left = 0;
    var right = 0;
    var bottom = 0;

    // Search from top and bottom to find first rows with non transparent pixel
    for (var y = 0; y < h && !found; y += 1) {
      for (var x = 0; x < w; x += 1) {
        if (pixelData[indexA++] && !top) {
          top = y + 1;
          // Top and bottom found then stop the search
          if (bottom) {
            found = true;
            break;
          }
        }
        if (pixelData[indexB--] && !bottom) {
          bottom = h - y - 1;
          // Top and bottom found then stop the search
          if (top) {
            found = true;
            break;
          }
        }
      }
      // Image is completely blank so do nothing
      if (y > h - y && !top && !bottom) {
        return false;
      }
    }

    // Correct the top
    top -= 1;
    // Reset the search
    found = false;

    // Search from left and right to find first column with pixel
    for (var x = 0; x < w && !found; x += 1) {
      indexA = top * w + x;
      indexB = top * w + (w - x - 1);
      for (var y = top; y <= bottom; y += 1) {
        if (pixelData[indexA] && !left) {
          left = x + 1;
          // If left and right found then stop the search
          if (right) {
            found = true;
            break;
          }
        }
        if (pixelData[indexB] && !right) {
          right = w - x - 1;
          // If left and right found then stop the search
          if (left) {
            found = true;
            break;
          }
        }
        indexA += w;
        indexB += w;
      }
    }

    // Correct the left
    left -= 1;

    // No need to crop if no change in size
    if (w === right - left + 1 && h === bottom - top + 1) {
      return true;
    }

    w = right - left + 1;
    h = bottom - top + 1;

    this.element.width = w;
    this.element.height = h;

    this.context.putImageData(imageData, -left, -top);

    return true;

  }

  /**
   * Method to export the canvas as a PNG image
   */
  CanvasContext.prototype.exportImage = function() {

    return this.element.toDataURL("image/img");

  }

  // Expose the class for public use
  return CanvasContext;

})();
